import React from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TransportFreightExchangeRateDto, TransportInvoiceDto } from '../../../types/transport';
import { formatMoneyPl } from '../../../utils/currency';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { formatLocalizedExchangeRate } from '../../../utils/numberFormatting';
import { compareInvoiceWithPlan } from '../transportInvoice';
import './TransportInvoice.css';

const { Text } = Typography;

interface Props {
  invoice: TransportInvoiceDto | null;
  /** Legacy free-text number of a truck without a recorded invoice. */
  legacyNumber?: string;
  /** NBP rate of a foreign-currency invoice (FT.3: last table before the issue day). */
  exchangeRate?: TransportFreightExchangeRateDto | null;
}

const Field: React.FC<{ label: React.ReactNode; children: React.ReactNode; testId?: string; wide?: boolean }> = ({ label, children, testId, wide }) => (
  <div className={`transport-detail-field${wide ? ' transport-invoice__field--wide' : ''}`} data-testid={testId}>
    <Text type="secondary" className="transport-detail-field__label">{label}</Text>
    <div className="transport-detail-field__value transport-invoice__value">{children}</div>
  </div>
);

/** Body of the „Faktura przewoźnika” section on the truck card. */
export default function TransportInvoiceSummary({ invoice, legacyNumber, exchangeRate = null }: Props) {
  const { t } = useTranslation(['transport']);

  if (!invoice) {
    return (
      <p className="transport-invoice__empty" data-testid="transport-invoice-empty">
        {legacyNumber
          ? t('transport:invoice.legacyNumber', { number: legacyNumber })
          : t('transport:invoice.empty')}
      </p>
    );
  }

  const { difference } = compareInvoiceWithPlan(invoice);
  const differenceClass = difference == null || difference === 0
    ? ''
    : difference > 0 ? 'transport-invoice__difference--up' : 'transport-invoice__difference--down';

  return (
    <div className="transport-invoice__grid" data-testid="transport-invoice-summary">
      <Field label={t('transport:invoice.number')} testId="transport-invoice-number">{invoice.number}</Field>
      <Field label={t('transport:invoice.issueDate')}>{formatDateEuropean(invoice.issueDate)}</Field>
      <Field label={t('transport:invoice.amount')} testId="transport-invoice-amount">
        {formatMoneyPl(invoice.amount, invoice.currency)}
      </Field>
      <Field label={t('transport:invoice.plan')} testId="transport-invoice-plan">
        {invoice.plannedRate == null ? '—' : formatMoneyPl(invoice.plannedRate, invoice.plannedCurrency || 'PLN')}
        {difference != null && difference !== 0 ? (
          <span className={`transport-invoice__sub ${differenceClass}`} data-testid="transport-invoice-difference">
            {t('transport:invoice.difference', {
              amount: `${difference > 0 ? '+' : '−'}${formatMoneyPl(Math.abs(difference), invoice.currency)}`,
            })}
          </span>
        ) : null}
      </Field>
      {invoice.currency !== 'PLN' ? (
        <Field label={t('transport:invoice.exchangeRate')} testId="transport-invoice-exchange-rate" wide>
          {exchangeRate ? (
            <>
              <span className="transport-invoice__nowrap">{t('transport:invoice.exchangeRateValue', {
                rate: formatLocalizedExchangeRate(exchangeRate.rate, 'pl'),
                date: exchangeRate.effectiveDate ? formatDateEuropean(exchangeRate.effectiveDate) : '—',
              })}</span>
              {exchangeRate.tableNumber ? (
                <span className="transport-invoice__sub transport-invoice__nowrap">
                  {t('transport:invoice.exchangeRateTable', { table: exchangeRate.tableNumber })}
                </span>
              ) : null}
            </>
          ) : '—'}
        </Field>
      ) : null}
    </div>
  );
}
